import { USER_INFO } from '@/config/permissions'
import {
  loginByPassword as apiLoginByPassword,
  loginBySms as apiLoginBySms,
  logoutApi,
  getCurrent,
  getMyPermCodes
} from '@/api/auth'

// ── 前后台会话隔离 ──
// 前台：图书馆馆员 / 读者，登录走 /front/login
// 后台：平台运营人员，登录走 /admin/auth
const FRONT_TOKEN_KEY = 'wb_front_token'
const FRONT_USER_KEY = 'wb_front_user_info'
const FRONT_PERMS_KEY = 'wb_front_perms'
const ADMIN_TOKEN_KEY = 'wb_admin_token'
const ADMIN_USER_KEY = 'wb_admin_user_info'
const ADMIN_PERMS_KEY = 'wb_admin_perms'

// 旧版本兼容（仅作为前台 fallback 读取，写入会迁移到新 key）
const LEGACY_FRONT_TOKEN_KEY = 'wb_session_token'
const LEGACY_FRONT_USER_KEY = 'wb_user_info'

function read(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key) || ''
}
function readJson(key) {
  const raw = read(key)
  if (!raw) return null
  try { return JSON.parse(raw) } catch (_) { return null }
}

function readFrontToken() {
  return read(FRONT_TOKEN_KEY) || read(LEGACY_FRONT_TOKEN_KEY)
}
function readFrontUser() {
  return readJson(FRONT_USER_KEY) || readJson(LEGACY_FRONT_USER_KEY)
}
function readFrontPerms() {
  return readJson(FRONT_PERMS_KEY) || null
}
function writeFrontPerms(perms) {
  const list = Array.isArray(perms) ? perms : []
  // 与 token 同源：token 在 localStorage 则同步到 localStorage，反之 sessionStorage
  const persist = !!localStorage.getItem(FRONT_TOKEN_KEY)
  const storage = persist ? localStorage : sessionStorage
  const other = persist ? sessionStorage : localStorage
  storage.setItem(FRONT_PERMS_KEY, JSON.stringify(list))
  other.removeItem(FRONT_PERMS_KEY)
}
function clearFrontPerms() {
  localStorage.removeItem(FRONT_PERMS_KEY)
  sessionStorage.removeItem(FRONT_PERMS_KEY)
}
function readAdminToken() { return read(ADMIN_TOKEN_KEY) }
function readAdminUser() { return readJson(ADMIN_USER_KEY) }
function readAdminPerms() { return readJson(ADMIN_PERMS_KEY) || null }
function writeAdminPerms(perms) {
  const list = Array.isArray(perms) ? perms : []
  const persist = !!localStorage.getItem(ADMIN_TOKEN_KEY)
  const storage = persist ? localStorage : sessionStorage
  const other = persist ? sessionStorage : localStorage
  storage.setItem(ADMIN_PERMS_KEY, JSON.stringify(list))
  other.removeItem(ADMIN_PERMS_KEY)
}
function clearAdminPerms() {
  localStorage.removeItem(ADMIN_PERMS_KEY)
  sessionStorage.removeItem(ADMIN_PERMS_KEY)
}

function saveSession(tokenKey, userKey, token, userInfo, persist) {
  const storage = persist ? localStorage : sessionStorage
  const other = persist ? sessionStorage : localStorage
  storage.setItem(tokenKey, token)
  storage.setItem(userKey, JSON.stringify(userInfo || {}))
  other.removeItem(tokenKey)
  other.removeItem(userKey)
}

function clearSession(tokenKey, userKey) {
  localStorage.removeItem(tokenKey); sessionStorage.removeItem(tokenKey)
  localStorage.removeItem(userKey); sessionStorage.removeItem(userKey)
}

/** 提供给 request 拦截器按 side 取 token。 */
export function getTokenBySide(side) {
  return side === 'admin' ? readAdminToken() : readFrontToken()
}

export default {
  namespaced: true,
  state: {
    // 前台会话（保留旧字段名，避免大面积改动）
    token: readFrontToken(),
    userInfo: readFrontUser(),

    // 后台会话
    adminToken: readAdminToken(),
    adminUserInfo: readAdminUser(),

    roles: USER_INFO.roles,
    role: USER_INFO.role,
    // 优先用本地缓存的真实权限码（页面刷新场景）；未登录无权限码，前台按钮统一按 hasPermission 自然隐藏
    permissions: (readAdminToken() ? readAdminPerms() : readFrontPerms()) || readFrontPerms() || readAdminPerms() || [],
    // 后端 X-Menu-Version：每次 invalidateAllPermCaches 后递增；空 = 还没拿到过
    adminMenuVersion: '',
    // 防止短时间内并发 refetch 时重复 toast
    _adminMenuRefreshing: false,
    // 前台消息未读数（用于个人中心左侧菜单红点联动）
    _messageUnread: 0
  },
  mutations: {
    SET_TOKEN(state, token) { state.token = token },
    SET_USER_INFO(state, userInfo) { state.userInfo = userInfo },
    SET_ADMIN_TOKEN(state, token) { state.adminToken = token },
    SET_ADMIN_USER_INFO(state, userInfo) { state.adminUserInfo = userInfo },
    SET_ROLES(state, roles) { state.roles = roles },
    SET_PERMISSIONS(state, permissions) { state.permissions = permissions },
    SET_ROLE(state, role) { state.role = role },
    SET_ADMIN_MENU_VERSION(state, v) { state.adminMenuVersion = v },
    SET_ADMIN_MENU_REFRESHING(state, b) { state._adminMenuRefreshing = b },
    // 前台消息未读数（用于个人中心左侧菜单红点联动）
    SET_MESSAGE_UNREAD(state, count) { state._messageUnread = count || 0 }
  },
  actions: {
    async loginByPassword({ dispatch }, payload) {
      const res = await apiLoginByPassword(payload)
      await dispatch('applyLoginResult', { data: res.data, persist: !!payload.remember7Days })
      return res.data
    },
    async loginBySms({ dispatch }, payload) {
      const res = await apiLoginBySms(payload)
      await dispatch('applyLoginResult', { data: res.data, persist: false })
      return res.data
    },
    /** 前台（馆员/读者）登录结果落盘 */
    async applyLoginResult({ commit, dispatch }, { data, persist }) {
      const token = data.sessionToken
      const userInfo = data.userInfo || data
      saveSession(FRONT_TOKEN_KEY, FRONT_USER_KEY, token, userInfo, persist)
      // 清理旧 key 残留，避免新旧并存
      clearSession(LEGACY_FRONT_TOKEN_KEY, LEGACY_FRONT_USER_KEY)
      commit('SET_TOKEN', token)
      commit('SET_USER_INFO', userInfo)
      if (userInfo && userInfo.role) commit('SET_ROLE', userInfo.role)
      await dispatch('fetchFrontPerms')
    },
    /** 拉取当前前台用户的真实权限码（来自后端 /front/auth/permission/codes） */
    async fetchFrontPerms({ state, commit }) {
      if (!state.token) return []
      try {
        const res = await getMyPermCodes()
        const codes = (res && res.data && res.data.codes) || []
        commit('SET_PERMISSIONS', codes)
        writeFrontPerms(codes)
        return codes
      } catch (e) {
        return state.permissions
      }
    },
    /** 后台（平台运营人员）登录结果落盘 */
    async applyAdminLoginResult({ commit, dispatch }, { data, persist }) {
      const token = data.sessionToken || data.token
      const userInfo = data.userInfo || data
      saveSession(ADMIN_TOKEN_KEY, ADMIN_USER_KEY, token, userInfo, !!persist)
      commit('SET_ADMIN_TOKEN', token)
      commit('SET_ADMIN_USER_INFO', userInfo)
      await dispatch('fetchAdminPerms')
    },
    /** 拉取当前后台用户的权限码（来自后端 /admin/auth/permission/codes） */
    async fetchAdminPerms({ state, commit }) {
      if (!state.adminToken) return []
      try {
        const { getMyPermCodes: getAdminPermCodes } = await import('@/api/permission')
        const res = await getAdminPermCodes()
        const codes = (res && res.data && res.data.codes) || []
        commit('SET_PERMISSIONS', codes)
        writeAdminPerms(codes)
        return codes
      } catch (e) {
        return state.permissions
      }
    },
    async refreshCurrent({ state, commit, dispatch }) {
      if (!state.token) return null
      try {
        const res = await getCurrent()
        commit('SET_USER_INFO', res.data)
        if (res.data && res.data.role) commit('SET_ROLE', res.data.role)
        await dispatch('fetchFrontPerms')
        return res.data
      } catch (e) {
        return null
      }
    },
    /** 前台登出（不影响后台会话） */
    async logout({ commit, state }) {
      if (state.token) {
        try { await logoutApi() } catch (e) { /* 静默 */ }
      }
      clearSession(FRONT_TOKEN_KEY, FRONT_USER_KEY)
      clearSession(LEGACY_FRONT_TOKEN_KEY, LEGACY_FRONT_USER_KEY)
      clearFrontPerms()
      commit('SET_TOKEN', '')
      commit('SET_USER_INFO', null)
      commit('SET_ROLES', [])
      commit('SET_PERMISSIONS', [])
      commit('SET_ROLE', '')
    },
    /**
     * 后端通过 X-Menu-Version 头通知菜单/角色配置变更。
     * 仅在已登录、版本号确实变化时重拉权限码并 toast 一次。
     */
    async onAdminMenuVersionChange({ state, commit }, version) {
      if (!version || !state.adminToken) return
      // 首次收到（state 中无值）：直接记录，不算"变更"
      if (!state.adminMenuVersion) {
        commit('SET_ADMIN_MENU_VERSION', version)
        return
      }
      if (state.adminMenuVersion === version) return
      if (state._adminMenuRefreshing) return
      commit('SET_ADMIN_MENU_REFRESHING', true)
      try {
        const { getMyPermCodes: getAdminPermCodes } = await import('@/api/permission')
        const res = await getAdminPermCodes()
        const codes = (res && res.data && res.data.codes) || []
        commit('SET_PERMISSIONS', codes)
        writeAdminPerms(codes)
        commit('SET_ADMIN_MENU_VERSION', version)
        try {
          const { Message } = await import('element-ui')
          Message.info({ message: '菜单/权限配置已更新，已为您刷新生效', duration: 3000 })
        } catch (_) { /* element-ui 通常已全局加载，导入失败可忽略 */ }
      } catch (_) {
        // 拉取失败：不更新 version，下次再试
      } finally {
        commit('SET_ADMIN_MENU_REFRESHING', false)
      }
    },
    /** 后台登出（不影响前台会话） */
    async adminLogout({ commit, state }) {
      if (state.adminToken) {
        try {
          const { adminLogout: apiAdminLogout } = await import('@/api/permission')
          await apiAdminLogout()
        } catch (e) { /* 静默：服务端失效失败也要继续清本地 */ }
      }
      clearSession(ADMIN_TOKEN_KEY, ADMIN_USER_KEY)
      clearAdminPerms()
      commit('SET_ADMIN_TOKEN', '')
      commit('SET_ADMIN_USER_INFO', null)
      commit('SET_ADMIN_MENU_VERSION', '')
      commit('SET_PERMISSIONS', [])
    }
  }
}
